import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import api from '../lib/api'

function fmtMoney(cents?: number | null, currency = 'USD') {
  if (typeof cents !== 'number') return 'Pending'
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100)
}

function fmtDate(value?: string | null) {
  return value ? new Date(value).toLocaleString() : 'Not specified'
}

export default function QuoteResponsePage() {
  const params = useParams<{ token: string }>()
  const token = params.token || ''
  const qc = useQueryClient()
  const [declineReason, setDeclineReason] = useState('')
  const [feedback, setFeedback] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [customerPortalUrl, setCustomerPortalUrl] = useState<string | null>(null)

  const quoteQuery = useQuery({
    queryKey: ['quote-response', token],
    queryFn: () => api.quoteResponse.get(token).then((r) => r.data.quoteResponse),
    enabled: !!token,
  })

  const acceptMutation = useMutation({
    mutationFn: () => api.quoteResponse.accept(token),
    onSuccess: (response) => {
      qc.setQueryData(['quote-response', token], response.data.quoteResponse)
      setError(null)
      setCustomerPortalUrl(response.data.customerPortalUrl ?? null)
      setFeedback(response.data.nextStep || 'Quote accepted. A dispatcher will follow up shortly.')
    },
    onError: (err: any) => {
      setFeedback(null)
      setError(err?.response?.data?.error ?? 'Unable to accept quote right now.')
    },
  })

  const declineMutation = useMutation({
    mutationFn: () => api.quoteResponse.decline(token, declineReason.trim() || undefined),
    onSuccess: (response) => {
      qc.setQueryData(['quote-response', token], response.data.quoteResponse)
      setError(null)
      setFeedback('Quote declined. Thank you for the update.')
    },
    onError: (err: any) => {
      setFeedback(null)
      setError(err?.response?.data?.error ?? 'Unable to decline quote right now.')
    },
  })

  const quote = quoteQuery.data
  const canAct = useMemo(() => quote?.status === 'quote_sent' || quote?.status === 'quoted', [quote?.status])

  const statusLabel = quote?.status ? quote.status.replace(/_/g, ' ') : ''

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6">
      <div className="mx-auto w-full max-w-3xl space-y-5">
        <header className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Quote Response</p>
          <h1 className="mt-2 text-2xl font-bold text-slate-900">{quote?.accountCompanyName || 'Diamond Dispatch'}</h1>
          <p className="mt-2 text-sm text-slate-600">Review your transport quote and choose Book It to reserve this rate.</p>
        </header>

        {quoteQuery.isLoading && <div className="rounded-xl bg-white p-5 text-sm text-slate-600 ring-1 ring-slate-200">Loading quote...</div>}
        {quoteQuery.isError && <div className="rounded-xl bg-red-50 p-5 text-sm text-red-700 ring-1 ring-red-200">Quote link is invalid or expired.</div>}

        {quote && (
          <section className="space-y-5">
            <div className="grid gap-4 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200 sm:grid-cols-2">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Customer</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{quote.customerName}</p>
                <p className="text-sm text-slate-600">{quote.customerEmail}</p>
                <p className="text-sm text-slate-600">{quote.customerPhone}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Vehicle</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{quote.vehicleYear ?? ''} {quote.vehicleMake ?? ''} {quote.vehicleModel ?? ''}</p>
                <p className="text-sm text-slate-600 capitalize">VIN status: {quote.vinStatus.replace('_', ' ')}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Pickup</p>
                <p className="mt-1 text-sm text-slate-900">{quote.pickupCity}, {quote.pickupState} {quote.pickupZip}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Delivery</p>
                <p className="mt-1 text-sm text-slate-900">{quote.deliveryCity}, {quote.deliveryState} {quote.deliveryZip}</p>
              </div>
            </div>

            <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
              <p className="text-xs uppercase tracking-wide text-slate-500">Quoted Price</p>
              <p className="mt-2 text-3xl font-bold text-slate-900">{fmtMoney(quote.quotedCustomerPriceCents, quote.quotedCurrency || 'USD')}</p>
              <p className="mt-1 text-xs text-slate-500">Quote expires: {fmtDate(quote.quoteExpiresAt)}</p>
              {quote.quoteMessage && <p className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">{quote.quoteMessage}</p>}
              {quote.paymentRequiredBeforePickup && (
                <p className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-700">
                  Payment note: {quote.paymentRequirementReason || 'Payment may be required before pickup.'}
                </p>
              )}
            </div>

            {feedback && <div className="rounded-xl bg-green-50 p-4 text-sm text-green-700 ring-1 ring-green-200">{feedback}</div>}
            {error && <div className="rounded-xl bg-red-50 p-4 text-sm text-red-700 ring-1 ring-red-200">{error}</div>}
            {customerPortalUrl && (
              <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-4 text-sm text-cyan-900">
                Customer portal ready. <a href={customerPortalUrl} className="font-semibold underline">Open the order completion workspace</a>
              </div>
            )}

            <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
              <p className="text-sm font-semibold text-slate-900">Status: <span className="capitalize">{statusLabel}</span></p>

              {canAct ? (
                <div className="mt-4 space-y-4">
                  <button
                    type="button"
                    onClick={() => acceptMutation.mutate()}
                    disabled={acceptMutation.isPending || declineMutation.isPending}
                    className="w-full rounded-lg bg-emerald-600 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {acceptMutation.isPending ? 'Booking...' : 'Book It'}
                  </button>

                  <div className="space-y-2">
                    <label className="text-xs font-medium uppercase tracking-wide text-slate-500">Decline reason (optional)</label>
                    <textarea
                      value={declineReason}
                      onChange={(e) => setDeclineReason(e.target.value)}
                      className="h-24 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                      placeholder="Tell us why this quote is not a fit."
                    />
                    <button
                      type="button"
                      onClick={() => declineMutation.mutate()}
                      disabled={acceptMutation.isPending || declineMutation.isPending}
                      className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                      {declineMutation.isPending ? 'Submitting...' : 'Decline Quote'}
                    </button>
                  </div>
                </div>
              ) : (
                <p className="mt-3 text-sm text-slate-600">
                  {quote.status === 'accepted' && 'This quote has already been accepted. A dispatcher will follow up with scheduling details.'}
                  {quote.status === 'declined' && 'This quote has been declined.'}
                  {quote.status !== 'accepted' && quote.status !== 'declined' && 'This quote is no longer actionable.'}
                </p>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  )
}
